import { format, parse, startOfWeek, getDay } from 'date-fns';
import { addDays, addWeeks, addMonths, addYears, startOfWeek as dfStartOfWeek } from 'date-fns';
import { dateFnsLocalizer } from 'react-big-calendar';
import { Dayjs } from 'dayjs';

import { LOCALES, REGULAR_EVENT_TYPES, PROJECT_EVENT_COLOR, CRM_EVENT_COLOR } from './constants';
import { ExtendedEvent, RecurrenceRule, RecurrencePattern } from './types';

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: LOCALES,
});

export const eventStyleGetter = (event: ExtendedEvent) => {
  let backgroundColor = '#3174ad';
  let borderColor = backgroundColor;

  if (event.type === 'regular' && event.eventType) {
    const cfg = REGULAR_EVENT_TYPES.find(c => c.type === event.eventType);
    backgroundColor = cfg?.color ?? backgroundColor;
    borderColor = backgroundColor;
  }

  if (event.type === 'project') {
    backgroundColor = PROJECT_EVENT_COLOR;
    borderColor = '#ff5252';
  }

  if (event.type === 'crm') {
    backgroundColor = CRM_EVENT_COLOR;
    borderColor = '#b07c0a';
  }

  const boxShadow = event.isOnlineMeeting
    ? '0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px ' + borderColor
    : '2px 2px 2px 0 rgba(0, 0, 0, 0.2)';

  return {
    style: {
      backgroundColor,
      borderRadius: 6,
      opacity: 0.9,
      color: '#fff',
      padding: '2px 6px',
      fontSize: 12,
      border: `1px solid ${borderColor}`,
      boxShadow,
      fontWeight: event.isOnlineMeeting ? '600' : '400',
    },
  } as const;
};

const ENUM_TO_LITERAL: Record<number, Exclude<RecurrencePattern, number>> = {
  0: 'none',
  1: 'daily',
  2: 'weekly',
  3: 'monthly',
  4: 'yearly',
};
const LITERAL_TO_ENUM: Record<Exclude<RecurrencePattern, number>, number> = {
  none: 0,
  daily: 1,
  weekly: 2,
  monthly: 3,
  yearly: 4,
};

const isValidPatternNumber = (pattern: number): pattern is keyof typeof ENUM_TO_LITERAL =>
  pattern >= 0 && pattern <= 4 && pattern in ENUM_TO_LITERAL;

const isValidPatternLiteral = (pattern: string): pattern is Exclude<RecurrencePattern, number> =>
  ['none', 'daily', 'weekly', 'monthly', 'yearly'].includes(pattern);

function normaliseRule(
  rule: RecurrenceRule,
): RecurrenceRule & { pattern: Exclude<RecurrencePattern, number> } {
  const p = rule.pattern;
  return {
    ...rule,
    pattern:
      typeof p === 'number' && isValidPatternNumber(p)
        ? ENUM_TO_LITERAL[p]
        : typeof p === 'string' && isValidPatternLiteral(p)
        ? p
        : 'none',
  };
}

function cloneEvent(base: ExtendedEvent, newStart: Date, ix: number): ExtendedEvent {
  const dur = base.end.getTime() - base.start.getTime();
  return {
    ...base,
    id: `${base.id}_${ix}`,
    start: newStart,
    end: new Date(newStart.getTime() + dur),
    parentEventId: base.id,
    isRecurring: true,
  };
}

function createInstanceDate(baseDate: Date, targetDate: Date): Date {
  const out = new Date(targetDate);
  out.setHours(
    baseDate.getHours(),
    baseDate.getMinutes(),
    baseDate.getSeconds(),
    baseDate.getMilliseconds(),
  );
  return out;
}

export function generateRecurringEvents(base: ExtendedEvent, rawRule: RecurrenceRule): ExtendedEvent[] {
  const rule = normaliseRule(rawRule);
  if (rule.pattern === 'none') return [base];

  const out: ExtendedEvent[] = [];
  const MAX = 200;
  const interval = Math.max(1, rule.interval || 1);

  const until: Date | null =
    rule.end.type === 'untilDate'
      ? (rule.end.date as Dayjs)?.toDate?.() ?? (rule.end.date as Date) ?? null
      : null;
  const maxCount = rule.end.type === 'after' ? rule.end.count ?? Infinity : Infinity;

  let added = 0;
  const push = (d: Date) => {
    if (until && d > until) return false;
    if (added >= maxCount || added >= MAX) return false;
    out.push(cloneEvent(base, d, added));
    added += 1;
    return true;
  };

  if (rule.pattern === 'daily') {
    let cur = new Date(base.start);
    while (push(cur)) cur = addDays(cur, interval);
    return out;
  }

  if (rule.pattern === 'weekly') {
    const wds = rule.byWeekday?.length ? [...rule.byWeekday].sort() : [base.start.getDay()];
    let weekCursor = new Date(base.start);
    while (added < MAX && added < maxCount) {
      for (const wd of wds) {
        const weekStart = dfStartOfWeek(weekCursor, { weekStartsOn: 0 });
        const targetDate = new Date(weekStart);
        targetDate.setDate(weekStart.getDate() + wd);
        const instanceDate = createInstanceDate(base.start, targetDate);
        const baseD = new Date(base.start);
        baseD.setHours(0, 0, 0, 0);
        const instD = new Date(instanceDate);
        instD.setHours(0, 0, 0, 0);
        if (instD < baseD) continue;
        if (!push(instanceDate)) return out;
      }
      weekCursor = addWeeks(weekCursor, interval);
    }
    return out;
  }

  if (rule.pattern === 'monthly') {
    let cur = new Date(base.start);
    const dom = cur.getDate();
    while (push(cur)) {
      cur = addMonths(cur, interval);
      const lastDomOfMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      cur.setDate(Math.min(dom, lastDomOfMonth));
    }
    return out;
  }

  if (rule.pattern === 'yearly') {
    let cur = new Date(base.start);
    while (push(cur)) cur = addYears(cur, interval);
    return out;
  }

  return out;
}

export const patternEnum = {
  toLiteral: (v: number): Exclude<RecurrencePattern, number> =>
    isValidPatternNumber(v) ? ENUM_TO_LITERAL[v] : 'none',
  toNumeric: (l: Exclude<RecurrencePattern, number>): number =>
    isValidPatternLiteral(l) ? LITERAL_TO_ENUM[l] : 0,
};
